import { StorefrontClient, storefront } from './storefront';

export interface MenuDraftRecipe { id: string; title: string; image?: string | null }
export interface MenuDraft { version: number; explicit: boolean; recipes: MenuDraftRecipe[]; pending?: unknown; unavailable_count?: number }
export const SAFE_MENU_ID = /^[A-Za-z0-9_-]{1,100}$/;
export function validMenuRecipe(value: unknown): MenuDraftRecipe | null {
  if (!value || typeof value !== 'object') return null;
  const r = value as MenuDraftRecipe;
  if (typeof r.id !== 'string' || !SAFE_MENU_ID.test(r.id) || typeof r.title !== 'string' || !r.title.trim() || r.title.length > 300) return null;
  // Allow catalog HTTPS images as well as same-origin paths, never executable URLs.
  const image = typeof r.image === 'string' && r.image.length <= 2000
    && (/^\/(?!\/)/.test(r.image) || /^https:\/\//i.test(r.image)) ? r.image : null;
  return { id: r.id, title: r.title, image };
}
function parseDraft(value: unknown): MenuDraft {
  const d = value as MenuDraft | null;
  if (!d || !Number.isSafeInteger(d.version) || d.version < 0 || typeof d.explicit !== 'boolean'
    || (d.unavailable_count !== undefined && (!Number.isSafeInteger(d.unavailable_count) || d.unavailable_count < 0 || d.unavailable_count > 50))
    || !Array.isArray(d.recipes) || d.recipes.length > 50) throw Error('invalid_menu_draft');
  const recipes = d.recipes.map(validMenuRecipe);
  if (recipes.some(r => !r) || new Set(recipes.map(r => r!.id)).size !== recipes.length
    || (!d.explicit && recipes.length)) throw Error('invalid_menu_draft');
  return { version: d.version, explicit: d.explicit, recipes: recipes as MenuDraftRecipe[], pending: d.pending, unavailable_count: d.unavailable_count };
}
export function createMenuDraftApi(opts: { base?: string; fetchImpl?: typeof fetch; client?: StorefrontClient } = {}) {
  // Production uses the existing shared client, not a second cookie bootstrap.
  // Explicit transports retain their own injectable client for isolated API tests.
  const client = opts.client ?? (opts.base !== undefined || opts.fetchImpl ? new StorefrontClient(opts) : storefront);
  async function call(body?: { expected_version: number; recipe_ids: string[] }): Promise<MenuDraft> {
    const payload = await (body ? client.menuDraft.save(body) : client.menuDraft.read());
    return parseDraft(payload?.draft);
  }
  return {
    read: () => call(),
    save(expectedVersion: number, recipeIds: string[]) {
      if (!Number.isSafeInteger(expectedVersion) || expectedVersion < 0 || recipeIds.length > 50
        || recipeIds.some(id => !SAFE_MENU_ID.test(id)) || new Set(recipeIds).size !== recipeIds.length) throw Error('invalid_menu_draft_request');
      return call({ expected_version: expectedVersion, recipe_ids: recipeIds });
    },
  };
}
export const menuDraftApi = createMenuDraftApi();
